import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import type { DocumentNumberingSettings } from '../../../types';
import { AppSelect } from '../../../components/AppSelect';
import {
  buildDocumentExportReferenceId,
  buildYearlyInvoiceReferenceId,
} from '../../../utils/documentExportReference';
import {
  buildDocumentNumber,
  DEFAULT_INVOICE_PREFIX_ABSCHLAG,
  DEFAULT_INVOICE_PREFIX_GUTSCHRIFT,
  DEFAULT_INVOICE_PREFIX_RECHNUNG,
  DEFAULT_INVOICE_PREFIX_SCHLUSS,
  DEFAULT_INVOICE_PREFIX_STORNO,
  DEFAULT_OFFERING_EXPORT_PREFIX,
  DEFAULT_OFFERING_NUMBER_PREFIX,
  DOCUMENT_NUMBER_DELIMITERS,
  INVOICE_FAMILY_LABELS,
  invoiceFamilyPrefixes,
  invoiceNumberBuildFields,
  invoicePeriodKey,
  invoicePrefixForLabel,
  invoiceSerialParseOptions,
  offeringNumberBuildFields,
  offeringPeriodKey,
  offeringSerialParseOptions,
  parseDocumentSerial,
  validateDocumentNumberFormatSettings,
  type DocumentNumberDelimiter,
  type DocumentNumberFormatSettings,
  type DocumentYearMode,
} from '../../../utils/documentNumberFormat';
import {
  documentSerialCounterInUse,
  serialStartForCurrentYear,
} from '../../../utils/documentNumberingFloor';
import { SettingsCard } from '../components/SettingsCard';
import { SettingsCardSavedStatus } from '../components/SettingsCardSavedStatus';
import { useModuleAccess } from '../../../hooks/useModuleAccess';
import { isDocumentTypeAvailable } from '../../../lib/documentTypeModules';
import {
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
  SETTINGS_INPUT_CLASS,
  SETTINGS_SELECT_TRIGGER_CLASS,
} from '../utils/settingsSectionStyles';

const fieldLabel = `block ${SETTINGS_FIELD_LABEL}`;
const hintClass = 'mt-1.5 text-xs text-app-text-subtle leading-relaxed';
const inputClass = SETTINGS_INPUT_CLASS;
const btnPrimary = SETTINGS_BTN_PRIMARY;
const selectTriggerClass = SETTINGS_SELECT_TRIGGER_CLASS;
const checkboxClass = 'rounded border-app-border text-pipedrive-blue focus:ring-pipedrive-blue/30';
const prefixInputClass = `${SETTINGS_INPUT_CLASS} disabled:bg-app-surface-muted disabled:text-slate-400 disabled:cursor-not-allowed`;
const delimiterSegmentWrap = 'inline-flex h-9 items-center gap-0.5 rounded-md bg-app-surface-muted p-0.5';
const delimiterSegmentBtn =
  'inline-flex h-8 min-w-8 items-center justify-center rounded-md px-2.5 text-sm font-medium tabular-nums transition-colors';

function CompactCheck({
  checked,
  disabled,
  onChange,
  children,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <label
      className={`inline-flex h-9 shrink-0 items-center gap-2 ${
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
      }`}
    >
      <input
        type="checkbox"
        className={checkboxClass}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="text-sm font-normal text-app-text">{children}</span>
    </label>
  );
}

function DelimiterPicker({
  value,
  onChange,
  ariaLabel,
}: {
  value: DocumentNumberDelimiter;
  onChange: (value: DocumentNumberDelimiter) => void;
  ariaLabel: string;
}) {
  return (
    <div className={delimiterSegmentWrap} role="group" aria-label={ariaLabel}>
      {DOCUMENT_NUMBER_DELIMITERS.map((d) => (
        <button
          key={d}
          type="button"
          aria-pressed={value === d}
          onClick={() => onChange(d)}
          className={`${delimiterSegmentBtn} ${
            value === d ? 'bg-app-surface text-app-text shadow-sm' : 'text-app-text-subtle hover:text-app-text'
          }`}
        >
          {d}
        </button>
      ))}
    </div>
  );
}

const DEFAULT_INVOICE_PREFIX_VALUES = {
  invoice_prefix_rechnung: DEFAULT_INVOICE_PREFIX_RECHNUNG,
  invoice_prefix_abschlag: DEFAULT_INVOICE_PREFIX_ABSCHLAG,
  invoice_prefix_schluss: DEFAULT_INVOICE_PREFIX_SCHLUSS,
  invoice_prefix_storno: DEFAULT_INVOICE_PREFIX_STORNO,
  invoice_prefix_gutschrift: DEFAULT_INVOICE_PREFIX_GUTSCHRIFT,
};

type RememberedPrefixes = typeof DEFAULT_INVOICE_PREFIX_VALUES;

function defaultRememberedPrefixes(): RememberedPrefixes {
  return { ...DEFAULT_INVOICE_PREFIX_VALUES };
}

function rememberNonEmptyPrefixes(target: RememberedPrefixes, format: DocumentNumberFormatSettings) {
  if (format.invoice_prefix_rechnung.trim()) target.invoice_prefix_rechnung = format.invoice_prefix_rechnung.trim();
  if (format.invoice_prefix_abschlag.trim()) target.invoice_prefix_abschlag = format.invoice_prefix_abschlag.trim();
  if (format.invoice_prefix_schluss.trim()) target.invoice_prefix_schluss = format.invoice_prefix_schluss.trim();
  if (format.invoice_prefix_storno.trim()) target.invoice_prefix_storno = format.invoice_prefix_storno.trim();
  if (format.invoice_prefix_gutschrift.trim()) {
    target.invoice_prefix_gutschrift = format.invoice_prefix_gutschrift.trim();
  }
}

function invoicePrefixesAreEmpty(format: DocumentNumberFormatSettings): boolean {
  return invoiceFamilyPrefixes(format).every((p) => !p.trim());
}

const YEAR_OPTIONS = [
  { value: 'year', label: 'Ja, volles Jahr (z. B. 2026)' },
  { value: 'yy', label: 'Ja, kurz (z. B. 26)' },
  { value: 'none', label: 'Nein' },
];

const SEQ_DIGIT_OPTIONS = [
  { value: '1', label: 'Ohne führende Nullen (1, 2, 37 …)' },
  { value: '2', label: '2 Stellen mit Nullen (01, 02 …)' },
  { value: '3', label: '3 Stellen mit Nullen (001, 002 …)' },
  { value: '4', label: '4 Stellen mit Nullen (0001 …)' },
  { value: '5', label: '5 Stellen mit Nullen (00001 …)' },
  { value: '6', label: '6 Stellen mit Nullen (000001 …)' },
];

function normalizeSeqDigitsForUi(digits: number): number {
  if (digits <= 1) return 1;
  if (digits >= 6) return 6;
  return digits;
}

function parseSerialDraft(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1 || n > 99999999) return null;
  return n;
}

function formatFromSettings(d: DocumentNumberingSettings): DocumentNumberFormatSettings {
  return {
    offering_number_prefix: d.offering_number_prefix,
    offering_number_year_mode: d.offering_number_year_mode,
    offering_number_seq_digits: d.offering_number_seq_digits,
    offering_number_include_month: d.offering_number_include_month,
    offering_number_delimiter: d.offering_number_delimiter,
    offering_export_prefix: d.offering_export_prefix,
    offering_export_use_version: d.offering_export_use_version,
    invoice_number_year_mode: d.invoice_number_year_mode,
    invoice_number_seq_digits: d.invoice_number_seq_digits,
    invoice_number_include_month: d.invoice_number_include_month,
    invoice_number_delimiter: d.invoice_number_delimiter,
    invoice_prefix_rechnung: d.invoice_prefix_rechnung,
    invoice_prefix_abschlag: d.invoice_prefix_abschlag,
    invoice_prefix_schluss: d.invoice_prefix_schluss,
    invoice_prefix_storno: d.invoice_prefix_storno,
    invoice_prefix_gutschrift: d.invoice_prefix_gutschrift,
  };
}

function nextSerialBaseline(
  observedMax: number,
  start: number | null,
  startYear: string | null,
  yearKey: string,
  previewSerial: number,
): number {
  if (documentSerialCounterInUse(observedMax)) {
    return Math.max(observedMax + 1, previewSerial);
  }
  const configured = serialStartForCurrentYear(start, startYear, yearKey);
  return configured ?? previewSerial;
}

/** Extract the running serial from a preview number — never the year segment. */
function parseSerialFromPreview(
  num: string,
  prefixes: string[],
  yearMode: DocumentYearMode,
  yearKey: string,
  options?: Parameters<typeof parseDocumentSerial>[4],
): number {
  return parseDocumentSerial(num, prefixes, yearMode, yearKey, options) ?? 1;
}

/** Präfixfelder der Rechnungsfamilie mit dem Belegtyp, dessen Modul sie freischaltet. */
const INVOICE_PREFIX_FIELDS = [
  ['invoice_prefix_rechnung', 'Rechnung', 'invoice-prefix-rechnung', 'Rechnung'],
  ['invoice_prefix_abschlag', 'Abschlag', 'invoice-prefix-abschlag', 'Abschlagsrechnung'],
  ['invoice_prefix_schluss', 'Schluss', 'invoice-prefix-schluss', 'Schlussrechnung'],
  ['invoice_prefix_storno', 'Storno', 'invoice-prefix-storno', 'Stornorechnung'],
  ['invoice_prefix_gutschrift', 'Gutschrift', 'invoice-prefix-gutschrift', 'Gutschrift'],
] as const;

/** „A, B und C“ */
function joinGermanList(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} und ${items[items.length - 1]}`;
}

/** „Vorgangs-, Angebots- und Rechnungsnummern“ je nach sichtbaren Nummernkreisen. */
function numberKindsText(showOffers: boolean, showInvoices: boolean): string {
  const stems = ['Vorgangs', ...(showOffers ? ['Angebots'] : []), ...(showInvoices ? ['Rechnungs'] : [])];
  return joinGermanList(stems.map((stem, i) => (i === stems.length - 1 ? `${stem}nummern` : `${stem}-`)));
}

export function DocumentNumberingSettingsSection() {
  // Nummernkreise nur für Belegtypen gebuchter Module; Vorgangsnummern bleiben immer.
  const { loaded: modulesLoaded, modules } = useModuleAccess();
  const showOfferNumbers = modulesLoaded && isDocumentTypeAvailable('Angebot', modules);
  const visibleInvoiceLabels = React.useMemo(
    () => (modulesLoaded ? INVOICE_FAMILY_LABELS.filter((label) => isDocumentTypeAvailable(label, modules)) : []),
    [modulesLoaded, modules],
  );
  const showInvoiceNumbers = visibleInvoiceLabels.length > 0;
  const [loaded, setLoaded] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const [format, setFormat] = React.useState<DocumentNumberFormatSettings | null>(null);
  const [baselineFormat, setBaselineFormat] = React.useState<DocumentNumberFormatSettings | null>(null);

  const [offeringSerialDraft, setOfferingSerialDraft] = React.useState('1');
  const [exportSerialDraft, setExportSerialDraft] = React.useState('1');
  const [invoiceSerialDraft, setInvoiceSerialDraft] = React.useState('1');
  const [baselineOfferingSerial, setBaselineOfferingSerial] = React.useState(1);
  const [baselineExportSerial, setBaselineExportSerial] = React.useState(1);
  const [baselineInvoiceSerial, setBaselineInvoiceSerial] = React.useState(1);
  const [invoiceObservedSerialMax, setInvoiceObservedSerialMax] = React.useState(0);
  const [offeringObservedSerialMax, setOfferingObservedSerialMax] = React.useState(0);
  const [exportObservedSerialMax, setExportObservedSerialMax] = React.useState(0);
  const lastPrefixesRef = React.useRef<RememberedPrefixes>(defaultRememberedPrefixes());
  const [omitInvoicePrefix, setOmitInvoicePrefix] = React.useState(false);

  const applyFromApi = React.useCallback((d: DocumentNumberingSettings) => {
    const nextFormat = formatFromSettings(d);
    nextFormat.offering_number_seq_digits = normalizeSeqDigitsForUi(nextFormat.offering_number_seq_digits);
    nextFormat.invoice_number_seq_digits = normalizeSeqDigitsForUi(nextFormat.invoice_number_seq_digits);
    rememberNonEmptyPrefixes(lastPrefixesRef.current, nextFormat);
    setOmitInvoicePrefix(invoicePrefixesAreEmpty(nextFormat));
    setFormat(nextFormat);
    setBaselineFormat(nextFormat);

    const offeringYearKey = offeringNumberBuildFields(nextFormat).yearKey;
    const invoiceYearKey = invoiceNumberBuildFields(nextFormat).yearKey;
    const offeringPeriod = offeringPeriodKey(nextFormat);
    const invoicePeriod = invoicePeriodKey(nextFormat);
    const offeringObserved = d.offering_observed_serial_max ?? 0;
    const exportObserved = d.offering_export_observed_serial_max ?? 0;
    const invoiceObserved = d.invoice_observed_serial_max ?? 0;
    const offeringPrefixes = nextFormat.offering_number_prefix.trim()
      ? Array.from(new Set([DEFAULT_OFFERING_NUMBER_PREFIX, nextFormat.offering_number_prefix]))
      : [''];
    const exportPrefixes = nextFormat.offering_export_prefix.trim()
      ? Array.from(new Set([DEFAULT_OFFERING_EXPORT_PREFIX, nextFormat.offering_export_prefix]))
      : [''];
    const invoicePrefixes = invoiceFamilyPrefixes(nextFormat).every((p) => !p.trim())
      ? ['']
      : Array.from(new Set(['RE', 'AR', 'SR', 'ST', 'GS', ...invoiceFamilyPrefixes(nextFormat)]));
    const offeringPreviewSerial = parseSerialFromPreview(
      d.preview_next_offering_display_id,
      offeringPrefixes,
      nextFormat.offering_number_year_mode,
      offeringYearKey,
      offeringSerialParseOptions(nextFormat),
    );
    const invoicePreviewSerial = parseSerialFromPreview(
      d.preview_next_invoice_reference_id,
      invoicePrefixes,
      nextFormat.invoice_number_year_mode,
      invoiceYearKey,
      invoiceSerialParseOptions(nextFormat),
    );
    const exportNext = Math.max(
      1,
      d.offering_export_next_serial ||
        parseSerialFromPreview(
          d.preview_next_offering_export_reference_id,
          exportPrefixes,
          nextFormat.offering_number_year_mode,
          offeringYearKey,
          offeringSerialParseOptions(nextFormat),
        ),
    );

    setOfferingObservedSerialMax(offeringObserved);
    setExportObservedSerialMax(exportObserved);
    setInvoiceObservedSerialMax(invoiceObserved);

    const offeringNext = nextSerialBaseline(
      offeringObserved,
      d.offering_serial_start,
      d.offering_serial_start_year,
      offeringPeriod,
      offeringPreviewSerial,
    );
    const invoiceNext = nextSerialBaseline(
      invoiceObserved,
      d.invoice_serial_start,
      d.invoice_serial_start_year,
      invoicePeriod,
      invoicePreviewSerial,
    );
    setBaselineOfferingSerial(offeringNext);
    setBaselineExportSerial(exportNext);
    setBaselineInvoiceSerial(invoiceNext);
    setOfferingSerialDraft(String(offeringNext));
    setExportSerialDraft(String(exportNext));
    setInvoiceSerialDraft(String(invoiceNext));
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setError(null);
    setSaved(false);
    fetch('/api/settings/document-numbering', { credentials: 'include' })
      .then(async (r) => {
        if (!r.ok) {
          throw new Error((await r.json().catch(() => null))?.error || 'Einstellungen konnten nicht geladen werden.');
        }
        return r.json() as Promise<DocumentNumberingSettings>;
      })
      .then((d) => {
        if (cancelled) return;
        applyFromApi(d);
        setLoaded(true);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Laden fehlgeschlagen.');
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [applyFromApi]);

  const updateFormat = (patch: Partial<DocumentNumberFormatSettings>) => {
    setFormat((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      if (patch.offering_number_year_mode === 'none') next.offering_number_include_month = false;
      if (patch.invoice_number_year_mode === 'none') next.invoice_number_include_month = false;
      if (next.offering_number_include_month && next.offering_number_year_mode === 'none') {
        next.offering_number_year_mode = 'yy';
      }
      if (next.invoice_number_include_month && next.invoice_number_year_mode === 'none') {
        next.invoice_number_year_mode = 'yy';
      }
      rememberNonEmptyPrefixes(lastPrefixesRef.current, next);
      return next;
    });
    setSaved(false);
    setError(null);
  };

  const offeringSerial = parseSerialDraft(offeringSerialDraft);
  const exportSerial = parseSerialDraft(exportSerialDraft);
  const invoiceSerial = parseSerialDraft(invoiceSerialDraft);

  const offeringPreview = React.useMemo(() => {
    if (!format || offeringSerial == null) return '—';
    const fields = offeringNumberBuildFields(format);
    return buildDocumentNumber({
      prefix: format.offering_number_prefix,
      ...fields,
      serial: offeringSerial,
    });
  }, [format, offeringSerial]);

  const offeringExportPreview = React.useMemo(() => {
    if (!format) return '—';
    if (format.offering_export_use_version) {
      if (offeringSerial == null || offeringPreview === '—') return '—';
      return buildDocumentExportReferenceId(
        offeringPreview,
        'Angebot',
        1,
        format.offering_export_prefix,
        format.offering_number_delimiter,
      );
    }
    if (exportSerial == null) return '—';
    const fields = offeringNumberBuildFields(format);
    return buildDocumentNumber({
      prefix: format.offering_export_prefix,
      ...fields,
      serial: exportSerial,
    });
  }, [format, offeringPreview, offeringSerial, exportSerial]);

  const invoicePreviews = React.useMemo(() => {
    if (!format || invoiceSerial == null) {
      return visibleInvoiceLabels.map((label) => ({ label, value: '—' }));
    }
    const fields = invoiceNumberBuildFields(format);
    return visibleInvoiceLabels.map((label) => ({
      label,
      value: buildYearlyInvoiceReferenceId(label, fields.yearKey, invoiceSerial, {
        prefix: invoicePrefixForLabel(format, label),
        ...fields,
      }),
    }));
  }, [format, invoiceSerial, visibleInvoiceLabels]);

  const formatDirty =
    format != null &&
    baselineFormat != null &&
    (format.offering_number_prefix !== baselineFormat.offering_number_prefix ||
      format.offering_number_year_mode !== baselineFormat.offering_number_year_mode ||
      format.offering_number_seq_digits !== baselineFormat.offering_number_seq_digits ||
      format.offering_export_prefix !== baselineFormat.offering_export_prefix ||
      format.offering_export_use_version !== baselineFormat.offering_export_use_version ||
      format.offering_number_include_month !== baselineFormat.offering_number_include_month ||
      format.offering_number_delimiter !== baselineFormat.offering_number_delimiter ||
      format.invoice_number_year_mode !== baselineFormat.invoice_number_year_mode ||
      format.invoice_number_seq_digits !== baselineFormat.invoice_number_seq_digits ||
      format.invoice_number_include_month !== baselineFormat.invoice_number_include_month ||
      format.invoice_number_delimiter !== baselineFormat.invoice_number_delimiter ||
      format.invoice_prefix_rechnung !== baselineFormat.invoice_prefix_rechnung ||
      format.invoice_prefix_abschlag !== baselineFormat.invoice_prefix_abschlag ||
      format.invoice_prefix_schluss !== baselineFormat.invoice_prefix_schluss ||
      format.invoice_prefix_storno !== baselineFormat.invoice_prefix_storno ||
      format.invoice_prefix_gutschrift !== baselineFormat.invoice_prefix_gutschrift);

  const serialDirty =
    (offeringSerial != null && offeringSerial !== baselineOfferingSerial) ||
    (exportSerial != null && exportSerial !== baselineExportSerial) ||
    (invoiceSerial != null && invoiceSerial !== baselineInvoiceSerial) ||
    offeringSerialDraft.trim() !== String(baselineOfferingSerial) ||
    exportSerialDraft.trim() !== String(baselineExportSerial) ||
    invoiceSerialDraft.trim() !== String(baselineInvoiceSerial);

  const dirty = formatDirty || serialDirty;

  const buildSavePayload = (): Record<string, unknown> => {
    if (!format) throw new Error('Einstellungen noch nicht geladen.');
    const formatError = validateDocumentNumberFormatSettings(format);
    if (formatError) throw new Error(formatError);
    if (offeringSerial == null) {
      throw new Error('Nächste Vorgangsnummer muss eine ganze Zahl ab 1 sein.');
    }
    if (!format.offering_export_use_version && exportSerial == null) {
      throw new Error('Nächste Angebotsnummer muss eine ganze Zahl ab 1 sein.');
    }
    if (invoiceSerial == null) {
      throw new Error('Nächste Rechnungsnummer muss eine ganze Zahl ab 1 sein.');
    }
    if (documentSerialCounterInUse(offeringObservedSerialMax) && offeringSerial < offeringObservedSerialMax + 1) {
      throw new Error('Die nächste Vorgangsnummer darf nicht unter dem bisherigen Stand liegen.');
    }
    if (
      !format.offering_export_use_version &&
      exportSerial != null &&
      documentSerialCounterInUse(exportObservedSerialMax) &&
      exportSerial < exportObservedSerialMax + 1
    ) {
      throw new Error('Die nächste Angebotsnummer darf nicht unter dem bisherigen Stand liegen.');
    }
    if (documentSerialCounterInUse(invoiceObservedSerialMax) && invoiceSerial < invoiceObservedSerialMax + 1) {
      throw new Error('Die nächste Rechnungsnummer darf nicht unter dem bisherigen Stand liegen.');
    }

    return {
      offering_number_prefix: format.offering_number_prefix.trim(),
      offering_number_year_mode: format.offering_number_year_mode,
      offering_number_seq_digits: format.offering_number_seq_digits,
      offering_number_include_month: format.offering_number_include_month,
      offering_number_delimiter: format.offering_number_delimiter,
      offering_export_prefix: format.offering_export_prefix.trim(),
      offering_export_use_version: format.offering_export_use_version,
      invoice_number_year_mode: format.invoice_number_year_mode,
      invoice_number_seq_digits: format.invoice_number_seq_digits,
      invoice_number_include_month: format.invoice_number_include_month,
      invoice_number_delimiter: format.invoice_number_delimiter,
      invoice_prefix_rechnung: format.invoice_prefix_rechnung.trim(),
      invoice_prefix_abschlag: format.invoice_prefix_abschlag.trim(),
      invoice_prefix_schluss: format.invoice_prefix_schluss.trim(),
      invoice_prefix_storno: format.invoice_prefix_storno.trim(),
      invoice_prefix_gutschrift: format.invoice_prefix_gutschrift.trim(),
      offering_serial_start: offeringSerial,
      ...(format.offering_export_use_version ? {} : { offering_export_serial_start: exportSerial }),
      invoice_serial_start: invoiceSerial,
    };
  };

  const requestSave = () => {
    setSaved(false);
    setError(null);
    try {
      buildSavePayload();
      setConfirmOpen(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    }
  };

  const save = async () => {
    setConfirmOpen(false);
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const payload = buildSavePayload();
      const res = await fetch('/api/settings/document-numbering', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((d as { error?: string }).error || 'Speichern fehlgeschlagen');
      applyFromApi(d as DocumentNumberingSettings);
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  React.useEffect(() => {
    if (!confirmOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) setConfirmOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [confirmOpen, saving]);

  if (!loaded || !format || !modulesLoaded) {
    return (
      <div className="-mx-3 -mt-3 md:mx-0 md:mt-0 space-y-3 md:space-y-5">
        <SettingsCard title="Nummernkreise für Dokumente">
          <InlineLoadingState label="Lade Einstellungen…" />
        </SettingsCard>
      </div>
    );
  }

  const offeringMin = documentSerialCounterInUse(offeringObservedSerialMax)
    ? offeringObservedSerialMax + 1
    : 1;
  const exportMin = documentSerialCounterInUse(exportObservedSerialMax)
    ? exportObservedSerialMax + 1
    : 1;
  const invoiceMin = documentSerialCounterInUse(invoiceObservedSerialMax)
    ? invoiceObservedSerialMax + 1
    : 1;

  return (
    <>
      <div className="-mx-3 -mt-3 md:mx-0 md:mt-0 space-y-3 md:space-y-5">
        {error ? (
          <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>
        ) : null}

        <SettingsCard
          title="Nummernkreise für Dokumente"
          description={`Format und nächste Nummer für ${joinGermanList([
            'Vorgänge',
            ...(showOfferNumbers ? ['Angebote'] : []),
            ...(showInvoiceNumbers ? ['Rechnungen'] : []),
          ])}. Bereits vergebene Nummern werden nicht unterschritten; systemvergebene Nummern sind nicht manuell änderbar.`}
          actions={
            <>
              <SettingsCardSavedStatus saved={saved} />
              <button type="button" className={btnPrimary} disabled={saving || !dirty} onClick={requestSave}>
                {saving ? 'Speichert…' : 'Speichern'}
              </button>
            </>
          }
        >
          <div className={`grid gap-6 ${showInvoiceNumbers ? 'lg:grid-cols-2' : ''}`}>
            {/* Vorgangs- / Angebotsnummer */}
            <div className="rounded-lg border border-app-border p-4 bg-app-surface-muted/60 space-y-5">
              <h3 className={SETTINGS_FIELD_LABEL}>{showOfferNumbers ? 'Vorgänge & Angebote' : 'Vorgänge'}</h3>
              <p className={hintClass}>
                {showOfferNumbers
                  ? 'Gemeinsames Format (Jahr/Stellen) für Vorgangs- und Angebotsnummer, jeweils mit eigenem Präfix. Ohne Versionsnummer hat die Angebotsnummer einen eigenen Zähler.'
                  : 'Format (Jahr/Stellen), Präfix und nächste Nummer für Vorgänge.'}
              </p>

              <div>
                <label className={fieldLabel} htmlFor="offering-number-year">
                  Jahr einbauen?
                </label>
                <div className="mt-1.5 flex flex-col gap-2 sm:flex-row sm:items-center">
                  <div className="min-w-0 flex-1">
                    <AppSelect
                      id="offering-number-year"
                      value={format.offering_number_year_mode}
                      onChange={(v) => updateFormat({ offering_number_year_mode: v as DocumentYearMode })}
                      options={YEAR_OPTIONS}
                      className="w-full"
                      triggerClassName={selectTriggerClass}
                    />
                  </div>
                  <CompactCheck
                    checked={format.offering_number_include_month}
                    disabled={format.offering_number_year_mode === 'none'}
                    onChange={(checked) => updateFormat({ offering_number_include_month: checked })}
                  >
                    Monat
                  </CompactCheck>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-[auto_minmax(0,1fr)] sm:items-end">
                <div>
                  <label className={fieldLabel} id="offering-number-delimiter-label">
                    Trennzeichen
                  </label>
                  <div className="mt-1.5">
                    <DelimiterPicker
                      value={format.offering_number_delimiter}
                      onChange={(v) => updateFormat({ offering_number_delimiter: v })}
                      ariaLabel="Trennzeichen Vorgänge und Angebote"
                    />
                  </div>
                </div>
                <div className="min-w-0">
                  <label className={fieldLabel} htmlFor="offering-number-digits">
                    Laufende Nummer
                  </label>
                  <div className="mt-1.5">
                    <AppSelect
                      id="offering-number-digits"
                      value={String(normalizeSeqDigitsForUi(format.offering_number_seq_digits))}
                      onChange={(v) => updateFormat({ offering_number_seq_digits: Number(v) || 1 })}
                      options={SEQ_DIGIT_OPTIONS}
                      className="w-full"
                      triggerClassName={selectTriggerClass}
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={fieldLabel} htmlFor="offering-serial-start">
                    Nächste Vorgangsnummer
                  </label>
                  <input
                    id="offering-serial-start"
                    type="number"
                    min={offeringMin}
                    max={99999999}
                    step={1}
                    value={offeringSerialDraft}
                    onChange={(e) => {
                      setOfferingSerialDraft(e.target.value);
                      setSaved(false);
                      setError(null);
                    }}
                    className={`${inputClass} mt-1.5 tabular-nums`}
                  />
                  {documentSerialCounterInUse(offeringObservedSerialMax) ? (
                    <p className={hintClass}>
                      Bisher bis {offeringObservedSerialMax}. Min. {offeringMin}.
                    </p>
                  ) : null}
                </div>
                {!showOfferNumbers ? null : !format.offering_export_use_version ? (
                  <div>
                    <label className={fieldLabel} htmlFor="offering-export-serial-start">
                      Nächste Angebotsnummer
                    </label>
                    <input
                      id="offering-export-serial-start"
                      type="number"
                      min={exportMin}
                      max={99999999}
                      step={1}
                      value={exportSerialDraft}
                      onChange={(e) => {
                        setExportSerialDraft(e.target.value);
                        setSaved(false);
                        setError(null);
                      }}
                      className={`${inputClass} mt-1.5 tabular-nums`}
                    />
                    {documentSerialCounterInUse(exportObservedSerialMax) ? (
                      <p className={hintClass}>
                        Bisher bis {exportObservedSerialMax}. Min. {exportMin}.
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <div>
                    <p className={fieldLabel}>Nächste Angebotsnummer</p>
                    <p className={`${hintClass} mt-1.5`}>
                      Folgt der Vorgangsnummer mit Versions-Suffix (-01, -02, …).
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <p className={SETTINGS_FIELD_LABEL}>Präfixe</p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="min-w-0">
                    <label className={fieldLabel} htmlFor="offering-number-prefix">
                      Vorgang
                    </label>
                    <input
                      id="offering-number-prefix"
                      type="text"
                      value={format.offering_number_prefix}
                      onChange={(e) => updateFormat({ offering_number_prefix: e.target.value })}
                      className={`${inputClass} mt-1.5`}
                      spellCheck={false}
                      autoComplete="off"
                      maxLength={16}
                    />
                  </div>
                  {showOfferNumbers ? (
                    <div className="min-w-0">
                      <label className={fieldLabel} htmlFor="offering-export-prefix">
                        Angebot
                      </label>
                      <input
                        id="offering-export-prefix"
                        type="text"
                        value={format.offering_export_prefix}
                        onChange={(e) => updateFormat({ offering_export_prefix: e.target.value })}
                        className={`${inputClass} mt-1.5`}
                        spellCheck={false}
                        autoComplete="off"
                        maxLength={16}
                      />
                    </div>
                  ) : null}
                </div>
              </div>

              {showOfferNumbers ? (
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1 rounded border-app-border text-pipedrive-blue focus:ring-pipedrive-blue/30"
                    checked={format.offering_export_use_version}
                    onChange={(e) => updateFormat({ offering_export_use_version: e.target.checked })}
                  />
                  <span>
                    <span className="block text-sm font-normal text-app-text">Versionsnummer bei Angebotsnummer</span>
                    <span className={hintClass}>
                      An: gleiche Basis wie Vorgang plus -01 → -02. Aus: eigener fortlaufender Zähler (unten
                      einstellbar).
                    </span>
                  </span>
                </label>
              ) : null}

              <div className="rounded-lg border border-app-border bg-app-surface px-4 py-3 space-y-2">
                <p className={SETTINGS_FIELD_LABEL}>Vorschau</p>
                <p className="text-sm text-app-text tabular-nums">
                  <span className="text-app-text-subtle">Vorgangsnummer:</span> {offeringPreview}
                </p>
                {showOfferNumbers ? (
                  <p className="text-sm text-app-text tabular-nums">
                    <span className="text-app-text-subtle">Angebotsnummer:</span> {offeringExportPreview}
                  </p>
                ) : null}
              </div>
            </div>

            {/* Rechnungen */}
            {showInvoiceNumbers ? (
              <div className="rounded-lg border border-app-border p-4 bg-app-surface-muted/60 space-y-5">
                <h3 className={SETTINGS_FIELD_LABEL}>Rechnungen</h3>
                <p className={hintClass}>
                  Ein gemeinsamer Zähler für Rechnung, Abschlag, Schluss und Storno, jeweils mit eigenem Präfix.
                </p>

                <div>
                  <label className={fieldLabel} htmlFor="invoice-number-year">
                    Jahr einbauen?
                  </label>
                  <div className="mt-1.5 flex flex-col gap-2 sm:flex-row sm:items-center">
                    <div className="min-w-0 flex-1">
                      <AppSelect
                        id="invoice-number-year"
                        value={format.invoice_number_year_mode}
                        onChange={(v) => updateFormat({ invoice_number_year_mode: v as DocumentYearMode })}
                        options={
                          omitInvoicePrefix
                            ? YEAR_OPTIONS.filter((o) => o.value !== 'none')
                            : YEAR_OPTIONS
                        }
                        className="w-full"
                        triggerClassName={selectTriggerClass}
                      />
                    </div>
                    <CompactCheck
                      checked={format.invoice_number_include_month}
                      disabled={format.invoice_number_year_mode === 'none'}
                      onChange={(checked) => updateFormat({ invoice_number_include_month: checked })}
                    >
                      Monat
                    </CompactCheck>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-[auto_minmax(0,1fr)] sm:items-end">
                  <div>
                    <label className={fieldLabel} id="invoice-number-delimiter-label">
                      Trennzeichen
                    </label>
                    <div className="mt-1.5">
                      <DelimiterPicker
                        value={format.invoice_number_delimiter}
                        onChange={(v) => updateFormat({ invoice_number_delimiter: v })}
                        ariaLabel="Trennzeichen Rechnungen"
                      />
                    </div>
                  </div>
                  <div className="min-w-0">
                    <label className={fieldLabel} htmlFor="invoice-number-digits">
                      Laufende Nummer
                    </label>
                    <div className="mt-1.5">
                      <AppSelect
                        id="invoice-number-digits"
                        value={String(normalizeSeqDigitsForUi(format.invoice_number_seq_digits))}
                        onChange={(v) => updateFormat({ invoice_number_seq_digits: Number(v) || 1 })}
                        options={SEQ_DIGIT_OPTIONS}
                        className="w-full"
                        triggerClassName={selectTriggerClass}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className={fieldLabel} htmlFor="invoice-serial-start">
                    Nächste Nummer
                  </label>
                  <input
                    id="invoice-serial-start"
                    type="number"
                    min={invoiceMin}
                    max={99999999}
                    step={1}
                    value={invoiceSerialDraft}
                    onChange={(e) => {
                      setInvoiceSerialDraft(e.target.value);
                      setSaved(false);
                      setError(null);
                    }}
                    className={`${inputClass} mt-1.5 tabular-nums`}
                  />
                  {documentSerialCounterInUse(invoiceObservedSerialMax) ? (
                    <p className={hintClass}>
                      Bisher vergeben bis {invoiceObservedSerialMax}. Mindestwert: {invoiceMin}.
                    </p>
                  ) : null}
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className={SETTINGS_FIELD_LABEL}>Präfixe</p>
                    <CompactCheck
                      checked={omitInvoicePrefix}
                      onChange={(omit) => {
                        setOmitInvoicePrefix(omit);
                        if (omit) {
                          updateFormat({
                            invoice_prefix_rechnung: '',
                            invoice_prefix_abschlag: '',
                            invoice_prefix_schluss: '',
                            invoice_prefix_storno: '',
                            invoice_prefix_gutschrift: '',
                            ...(format.invoice_number_year_mode === 'none'
                              ? { invoice_number_year_mode: 'yy' as const }
                              : {}),
                          });
                          return;
                        }
                        updateFormat({
                          invoice_prefix_rechnung: lastPrefixesRef.current.invoice_prefix_rechnung,
                          invoice_prefix_abschlag: lastPrefixesRef.current.invoice_prefix_abschlag,
                          invoice_prefix_schluss: lastPrefixesRef.current.invoice_prefix_schluss,
                          invoice_prefix_storno: lastPrefixesRef.current.invoice_prefix_storno,
                          invoice_prefix_gutschrift: lastPrefixesRef.current.invoice_prefix_gutschrift,
                        });
                      }}
                    >
                      Ohne Präfix
                    </CompactCheck>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {INVOICE_PREFIX_FIELDS.filter(([, , , documentType]) =>
                      (visibleInvoiceLabels as readonly string[]).includes(documentType),
                    ).map(([key, label, id]) => (
                      <div key={key}>
                        <label className={fieldLabel} htmlFor={id}>
                          {label}
                        </label>
                        <input
                          id={id}
                          type="text"
                          value={omitInvoicePrefix ? lastPrefixesRef.current[key] : format[key]}
                          onChange={(e) => updateFormat({ [key]: e.target.value })}
                          disabled={omitInvoicePrefix}
                          className={`${prefixInputClass} mt-1.5`}
                          spellCheck={false}
                          autoComplete="off"
                          maxLength={16}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-lg border border-app-border bg-app-surface px-4 py-3 space-y-2">
                  <p className={SETTINGS_FIELD_LABEL}>Vorschau (gleicher Zähler)</p>
                  {invoicePreviews.map(({ label, value }) => (
                    <p key={label} className="text-sm text-app-text tabular-nums">
                      <span className="text-app-text-subtle">{label}:</span> {value}
                    </p>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </SettingsCard>
      </div>

      {confirmOpen ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
          role="presentation"
          onClick={() => {
            if (!saving) setConfirmOpen(false);
          }}
        >
          <div
            className="bg-app-surface rounded-xl shadow-2xl max-w-md w-full border border-amber-200 overflow-hidden"
            role="dialog"
            aria-modal="true"
            aria-labelledby="document-numbering-confirm-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-amber-100 bg-amber-50">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 rounded-full bg-amber-100 p-2 text-amber-700 shrink-0">
                  <AlertTriangle className="h-5 w-5" aria-hidden />
                </div>
                <div className="min-w-0">
                  <h3 id="document-numbering-confirm-title" className="text-base font-semibold text-amber-950">
                    Achtung: Nummernkreise ändern?
                  </h3>
                  <p className="text-sm text-amber-950 mt-2 leading-relaxed">
                    Eine Änderung der Nummernkreise wirkt sich auf alle künftig vergebenen{' '}
                    {numberKindsText(showOfferNumbers, showInvoiceNumbers)} aus. Das kann erhebliche Auswirkungen auf Buchhaltung, Belegarchiv und
                    Schnittstellen zu Steuerberater oder DATEV haben, etwa durch Nummernlücken oder Abweichungen
                    zur bisherigen Belegfolge. Mit Monat in der Nummer startet die laufende Zahl im aktuellen
                    Monat neu; bereits vergebene Nummern bleiben unverändert.
                  </p>
                  <p className="text-sm text-amber-950 mt-3 leading-relaxed font-medium">
                    Bitte nur fortfahren, wenn die Anpassung mit deiner Buchhaltung abgestimmt ist.
                  </p>
                </div>
              </div>
            </div>
            <div className="px-5 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 bg-app-surface">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                disabled={saving}
                className={SETTINGS_BTN_OUTLINE}
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => void save()}
                disabled={saving}
                className="inline-flex h-9 items-center px-3.5 rounded-md bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 disabled:opacity-40"
              >
                {saving ? 'Speichert…' : 'Trotzdem speichern'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
